<template>
  <div class="bg-transparent min-h-screen">
    <div v-if="!loading" class="w-full max-w-7xl mx-auto p-4 sm:p-6">
      <header class="mb-10">
        <p
          class="text-xs font-bold uppercase tracking-widest text-[#4030C6] mb-2"
        >
          Communities
        </p>
        <h1 class="text-3xl sm:text-4xl font-bold text-[#111814]">Explorer</h1>
        <div
          class="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div class="relative w-full md:flex-1 md:max-w-xl">
            <span
              class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
            >
              <icon name="magnifying-glass" class="fill-slate-500" />
            </span>
            <input
              v-model="searchQuery"
              class="block w-full pl-12 py-3 bg-white/80 border border-slate-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#4030C6]/20 focus:border-[#4030C6] transition-all text-sm sm:text-base"
              :class="searchQuery ? 'pr-11' : 'pr-4'"
              placeholder="Search by name, DID or hash..."
            />
            <button
              v-if="searchQuery"
              type="button"
              @click="searchQuery = ''"
              class="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Clear search"
            >
              <i class="fas fa-times text-sm"></i>
            </button>
          </div>
          <div class="flex items-center gap-3 w-full md:w-auto">
            <span class="text-sm font-medium text-slate-500">Order by</span>
            <select
              v-model="sortMode"
              class="bg-white/80 border border-slate-200 rounded-lg py-2 pl-3 pr-10 text-sm font-semibold focus:ring-[#4030C6] focus:border-[#4030C6] appearance-none cursor-pointer w-full md:w-auto"
            >
              <option value="newest">Newest first</option>
              <option value="members">Most members</option>
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
            </select>
          </div>
        </div>
      </header>

      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-7"
      >
        <div
          v-for="(community, index) in displayedCommunities"
          :key="index"
          class="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
        >
          <router-link :to="'/community/' + community.did">
            <div
              class="relative h-36 sm:h-40 bg-gradient-to-br from-amber-100 to-rose-100"
            >
              <img
                :src="community.templateData.image"
                :alt="community.templateData.name"
                class="w-full h-full object-cover"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"
              ></div>
              <div
                class="absolute bottom-3 left-3 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-white shadow border border-slate-200 overflow-hidden"
              >
                <img
                  :src="community.issuerProfile.profileImage"
                  alt="Avatar"
                  class="w-full h-full object-cover"
                />
              </div>
            </div>
            <div class="p-4 sm:p-5">
              <h3
                class="text-xl sm:text-2xl font-bold text-[#111814] mb-3 sm:mb-4 group-hover:text-[#4030C6] transition-colors"
              >
                {{ community.issuerProfile.name }}
              </h3>
              <div class="flex items-center justify-between">
                <span
                  class="px-3 py-1 bg-slate-50 border border-slate-200 text-slate-700 rounded-full text-xs font-bold uppercase tracking-wider"
                >
                  {{ community.issuerProfile.sector || 'Community' }}
                </span>
                <div class="flex items-center gap-2 text-slate-700">
                  <icon name="user-group" class="fill-slate-500 h-4 w-4" />
                  <span class="text-sm font-semibold">{{
                    Math.max(0, community.members ?? 0)
                  }}</span>
                </div>
              </div>
              <p v-if="community.createdAt" class="mt-2 text-xs text-slate-500">
                Created {{ formatDate(community.createdAt) }}
              </p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
    <div v-else class="flex justify-center items-center gap-3 mt-10 h-full">
      <div class="flex justify-center mb-5">
        <Vue3Lottie
          :animationLink="require('/assets/images/loader-round.jpg')"
          :height="100"
          :width="100"
          :speed="1.5"
        />
      </div>
      <!-- <p class="text-lg text-slate-500">Loading...</p> -->
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted } from 'vue';
import { useCommunitiesStore } from '@/store/communities';

export default {
  setup() {
    const api = inject('api');
    const utils = inject('utils');
    const communitiesStore = useCommunitiesStore();

    const searchQuery = ref('');
    const sortMode = ref('newest');

    // Utilise les données du store
    const communities = computed(() => communitiesStore.communities);
    const loading = computed(() => communitiesStore.loading);

    const displayedCommunities = computed(() => {
      let communitiesToDisplay = [...communities.value];

      // Sort first (on ALL communities)
      if (sortMode.value === 'newest') {
        communitiesToDisplay.sort(
          (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
        );
      } else if (sortMode.value === 'members') {
        communitiesToDisplay.sort((a, b) => b.members - a.members);
      } else if (sortMode.value === 'az') {
        communitiesToDisplay.sort((a, b) =>
          a.issuerProfile.name.localeCompare(b.issuerProfile.name)
        );
      } else if (sortMode.value === 'za') {
        communitiesToDisplay.sort((a, b) =>
          b.issuerProfile.name.localeCompare(a.issuerProfile.name)
        );
      }

      // Filter by searchQuery: DID, community name, issuer name, sector
      const q = searchQuery.value?.toLowerCase();
      if (q && q.length > 1) {
        communitiesToDisplay = communitiesToDisplay.filter((community) => {
          const did = community.did?.toLowerCase() || '';
          const issuerName = community.issuerProfile?.name?.toLowerCase() || '';
          const sector = community.issuerProfile?.sector?.toLowerCase() || '';
          const communityName =
            community.templateData?.name?.toLowerCase() || '';
          return (
            did.includes(q) ||
            issuerName.includes(q) ||
            sector.includes(q) ||
            communityName.includes(q)
          );
        });
      }

      return communitiesToDisplay;
    });

    onMounted(async () => {
      // Charge d'abord le cache localStorage si disponible
      communitiesStore.loadFromLocalStorage();

      // Récupère les communautés (utilise le cache si valide)
      await communitiesStore.fetchCommunities(api, utils);
    });

    // Fonction pour forcer le refresh
    const refreshCommunities = async () => {
      await communitiesStore.fetchCommunities(api, utils, true);
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return {
      communities,
      displayedCommunities,
      loading,
      searchQuery,
      sortMode,
      refreshCommunities,
      formatDate,
    };
  },
};
</script>
